import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { USER_ROLES } from "./Auth.constants";
import {
  TAccountState,
  TApiKey,
  TOnboardingStatus,
  TUser,
  TUserData,
  TUserRole,
  TWhiteLabel,
} from "./Auth.types";

type TUserState = {
  user?: TUser;
  apiKey?: TApiKey;
  accountState?: TAccountState;
  token?: string;
  whiteLabel?: TWhiteLabel;
  selectedRole: TUserRole;
  passwordChange?: boolean;
};

type TUserActions = {
  setUserData: (data: TUserData) => void;
  setToken: (token: string) => void;
  setSelectedRole: (selectedRole: TUserRole) => void;
  setOsosOnboardingStatus: (newStatus: TOnboardingStatus) => void;
  setBillingOnboardingStatus: (newStatus: TOnboardingStatus) => void;
  logout: () => void;
};

const initialAuthState: TUserState = {
  user: undefined,
  apiKey: undefined,
  accountState: undefined,
  passwordChange: undefined,
  token: "",
  selectedRole: USER_ROLES.GUEST,
  whiteLabel: undefined,
};

export const useAuthStore = create<TUserState & TUserActions>()(
  devtools(
    persist(set => ({
      ...initialAuthState,
      setUserData: (userData: TUserData) =>
        set(() => ({
          user: userData.user,
          apiKey: userData.apiKey,
          accountState: userData.accountState,
          whiteLabel: userData.whiteLabel,
          passwordChange: userData.passwordChange,
        })),
      setToken: token => set(() => ({
        token,
      })),
      setSelectedRole: selectedRole => set(() => ({
        selectedRole,
      })),
      setOsosOnboardingStatus: (newStatus) => {
        set(state => ({
          accountState: {
            ...state.accountState,
            ososOnboarding: newStatus,
          },
        }));
      },
      setBillingOnboardingStatus: (newStatus) => {
        set(state => ({
          accountState: {
            ...state.accountState,
            billingOnboarding: newStatus,
          },
        }));
      },
      logout: () => {
        set(initialAuthState);
      },
    }),
    {
      name: "auth-store",
      version: 3,
    }),
  ),
);
